// Renaming a truck or the warehouse (stockLocations.rename): "Truck 1" becomes "Sam's truck". Its
// stock and its technician stay as they are. Opened from a location's name in Stock.

import { useEffect, useRef, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { SaveButton } from '../shared/SaveButton.tsx';
import type { StockLocation } from './model.ts';
import '../review/review.css';

export function RenameLocation({
  location,
  onClose,
  onRenamed,
}: {
  location: StockLocation;
  onClose: () => void;
  onRenamed: (name: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState(location.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const ready = !!name.trim() && name.trim() !== location.name;
  const rename = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('stockLocations.rename', { locationId: location.id, name: name.trim() });
      onRenamed(name.trim());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="rename-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void rename();
        }}
      >
        <h3 id="rename-title">Rename {location.name}</h3>
        <p className="muted">
          {location.kind === 'truck' ? 'Its stock and its technician' : 'Its stock'} stay as they are.
        </p>
        <label>
          Name
          <input className="field" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <SaveButton
            busy={busy}
            busyLabel="Renaming…"
            error={error}
            disabled={!ready}
            onClick={() => void rename()}
          >
            Rename
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}
